import { useEffect, useState, useCallback } from "react";
import {
  Trophy, Users2, PhoneCall, PhoneOff, AlarmClock, CheckSquare, CalendarCheck,
  CalendarPlus, TrendingUp, Gauge, Loader2, Search, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import DateRangePicker from "@/components/DateRangePicker";

const BAR = "#2F6BFF";
const fmtMins = (m) => (m == null ? "—" : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");
const scoreColor = (s) => (s == null ? "text-ink/40" : s >= 70 ? "text-emerald-600" : s >= 50 ? "text-amber-500" : "text-red-500");
const pct = (v) => (v == null ? "—" : `${v}%`);

const QUICK = [["", "All"], ["low_score", "Low score"], ["high_nc", "High not-contacted"], ["high_overdue", "High overdue"], ["low_meeting", "Low meeting %"], ["sla_breach", "SLA breach high"]];

const labelTone = (label) => ({
  "Excellent": "text-emerald-600", "Good": "text-emerald-600",
  "Needs Attention": "text-amber-500", "Critical": "text-red-500",
}[label] || "text-ink/40");
const breakdownTip = (b) => !b ? "" : [
  ["Contact discipline", b.contact_discipline, 25, "raw_percentage"],
  ["Response speed", b.response_speed, 15, "raw_score"],
  ["Follow-up completion", b.followup_completion, 20, "raw_percentage"],
  ["Overdue control", b.overdue_control, 15, "raw_score"],
  ["Meeting completion", b.meeting_completion, 15, "raw_percentage_or_score"],
  ["Stage movement", b.stage_movement, 10, "raw_percentage"],
].map(([n, c, w, k]) => `${n}: ${c[k]} → ${c.weighted_points}/${w} pts`).join("\n");

function ChartCard({ title, children, empty }) {
  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid={`chart-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h3 className="mb-3 font-sora text-sm font-semibold text-navy">{title}</h3>
      {empty ? <div className="flex h-48 items-center justify-center text-xs text-ink/40">No data for the selected filters.</div> : <div className="h-48">{children}</div>}
    </div>
  );
}
const SimpleBar = ({ data, color = BAR, angle = -20 }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={angle} textAnchor={angle ? "end" : "middle"} height={angle ? 55 : 30} />
      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
      <RTooltip />
      <Bar dataKey="value" radius={[4, 4, 0, 0]}>{data.map((e, i) => <Cell key={i} fill={color} />)}</Bar>
    </BarChart>
  </ResponsiveContainer>
);

export default function SalespersonPerformanceReport() {
  const { user, options } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [filters, setFilters] = useState({ created_from: "", created_to: "", manager: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" });
  const [opts, setOpts] = useState({ salespersons: [], sources: [], campaigns: [], services: [], managers: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState(null);
  const [tmeta, setTmeta] = useState({ total: 0, page: 1, pages: 1 });
  const [quick, setQuick] = useState("");
  const [sort, setSort] = useState("score_asc");
  const [page, setPage] = useState(1);

  const clean = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  useEffect(() => { api.get("/reports/filter-options").then(({ data }) => setOpts(data.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await minDelay(api.get("/reports/salesperson", { params: clean() })); setData(data.data); }
    catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [filters]); // eslint-disable-line
  const loadTable = useCallback(async () => {
    try { const { data } = await api.get("/reports/salesperson/table", { params: { ...clean(), quick, sort, page, page_size: 25 } });
      setRows(data.data.rows); setTmeta({ total: data.data.total, page: data.data.page, pages: data.data.pages });
    } catch { setRows([]); }
  }, [filters, quick, sort, page]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTable(); }, [loadTable]);
  useEffect(() => { setPage(1); }, [filters, quick, sort]);

  // Dynamic scoring engine (Phase 2) — additive, behind a toggle. Hardcoded score stays intact.
  const [dynOn, setDynOn] = useState(false);
  const [dynMap, setDynMap] = useState({});
  const [dynCfg, setDynCfg] = useState(null);
  useEffect(() => {
    if (!dynOn) return;
    api.get("/scoring/compute-batch", { params: { entity_type: "salesperson", ...clean() } })
      .then(({ data }) => { setDynCfg(data.data.config); const m = {}; (data.data.rows || []).forEach((x) => { m[x.entity_id] = x; }); setDynMap(m); })
      .catch(() => { setDynMap({}); });
  }, [dynOn, filters]); // eslint-disable-line
  const dynTip = (b) => (b || []).map((x) => `${x.label}: ${x.available ? x.score : "no data"} (${x.weight}%)`).join("\n");

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters({ created_from: "", created_to: "", manager: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" }); setQuick(""); };
  const hasFilters = Object.values(filters).some(Boolean) || quick;

  if (loading && !data) return <Loading label="Loading salesperson performance..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const empty = !data || data.empty;
  const k = data?.kpis, c = data?.charts;
  const selected = data?.selected;

  const cards = empty ? [] : [
    { label: "Assigned Leads", value: k.assigned, icon: Users2, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Contacted %", value: pct(k.contacted_pct), icon: PhoneCall, tone: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Not Contacted %", value: pct(k.nc_pct), icon: PhoneOff, tone: "text-red-500", bg: "bg-red-50" },
    { label: "SLA Breach %", value: pct(k.sla_breach_pct), icon: AlarmClock, tone: "text-orange-500", bg: "bg-orange-50" },
    { label: "Overdue Activities", value: k.overdue, icon: AlarmClock, tone: "text-amber-500", bg: "bg-amber-50" },
    { label: "Follow-up Completion %", value: pct(k.fu_completion_pct), icon: CheckSquare, tone: "text-navy", bg: "bg-mist" },
    { label: "Meetings Scheduled", value: k.mtg_sched, icon: CalendarPlus, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Meetings Completed", value: k.mtg_done, icon: CalendarCheck, tone: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Leads Moved Forward", value: k.moved_forward, icon: TrendingUp, tone: "text-purple-500", bg: "bg-purple-50" },
    { label: "Sales Execution Score", value: k.score == null ? "Insufficient data" : k.score, sub: k.score == null ? null : k.score_label, icon: Gauge, tone: scoreColor(k.score), bg: "bg-mist", big: true },
  ];

  return (
    <div className="space-y-6" data-testid="sp-perf-page">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Trophy className="h-7 w-7 text-loop" />Salesperson Performance</h1>
        <p className="flex items-center gap-2 text-sm text-ink/60">How well each salesperson is working their leads.{loading && data && <span className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky/70 bg-white p-3" data-testid="sp-filters">
        <DateRangePicker from={filters.created_from} to={filters.created_to} onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))} testid="sp-date" />
        {isAdmin && (
          <select value={filters.manager} onChange={(e) => setF("manager", e.target.value)} data-testid="f-manager" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
            <option value="">All managers</option>
            {opts.managers?.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <select value={filters.salesperson} onChange={(e) => setF("salesperson", e.target.value)} data-testid="f-salesperson" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All salespersons</option>
          {opts.salespersons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setF("source", e.target.value)} data-testid="f-source" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All sources</option>{opts.sources.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.campaign} onChange={(e) => setF("campaign", e.target.value)} data-testid="f-campaign" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All campaigns</option>{opts.campaigns.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.stage} onChange={(e) => setF("stage", e.target.value)} data-testid="f-stage" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All stages</option>{(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}</select>
        <select value={filters.quality} onChange={(e) => setF("quality", e.target.value)} data-testid="f-quality" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All quality</option>{(options?.qualities || []).map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filters.service} onChange={(e) => setF("service", e.target.value)} data-testid="f-service" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop"><option value="">All services</option>{opts.services.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        {hasFilters && <button onClick={clearFilters} data-testid="clear-filters" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist"><X className="h-3.5 w-3.5" />Clear</button>}
      </div>

      {empty ? (
        <EmptyState title="No report data found for the selected filters." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="sp-kpis">
            {cards.map((t) => (
              <div key={t.label} className="rounded-2xl border border-sky/70 bg-white p-4">
                <div className={`mb-2 inline-flex rounded-lg p-2 ${t.bg}`}><t.icon className={`h-4 w-4 ${t.tone}`} /></div>
                <p className={`font-sora font-bold text-navy ${t.big ? `text-xl ${scoreColor(k.score)}` : "text-2xl"}`}>{t.value}</p>
                <p className="text-xs text-ink/55">{t.label}</p>
                {t.big && t.sub && <p className={`mt-0.5 text-xs font-semibold ${labelTone(t.sub)}`} data-testid="sp-score-label">{t.sub}</p>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {selected ? (
              <>
                <ChartCard title="Lead Trend" empty={!c.selected?.trend?.length}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={c.selected.trend} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickFormatter={fmtDate} minTickGap={20} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} /><RTooltip />
                      <Line type="monotone" dataKey="total" stroke="#94a3b8" strokeWidth={2} dot={false} name="Total" />
                      <Line type="monotone" dataKey="contacted" stroke="#10b981" strokeWidth={2} dot={false} name="Contacted" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Stage Breakdown" empty={!c.selected?.stages?.length}><SimpleBar data={c.selected.stages} /></ChartCard>
                <ChartCard title="Not Contacted Aging" empty={c.selected?.aging?.every((x) => !x.value)}><SimpleBar data={c.selected.aging} color="#ef4444" angle={0} /></ChartCard>
                <ChartCard title="Activity → Meeting Funnel" empty={c.funnel.every((x) => !x.value)}><SimpleBar data={c.funnel} angle={-20} /></ChartCard>
              </>
            ) : (
              <>
                <ChartCard title="Execution Score Leaderboard" empty={!c.leaderboard.length}><SimpleBar data={c.leaderboard} color="#10b981" /></ChartCard>
                <ChartCard title="Not Contacted % by Salesperson" empty={!c.nc_by_salesperson.length}><SimpleBar data={c.nc_by_salesperson} color="#ef4444" /></ChartCard>
                <ChartCard title="Overdue Activities by Salesperson" empty={c.overdue_by_salesperson.every((x) => !x.value)}><SimpleBar data={c.overdue_by_salesperson} color="#f59e0b" /></ChartCard>
                <ChartCard title="Activity → Meeting Funnel" empty={c.funnel.every((x) => !x.value)}><SimpleBar data={c.funnel} angle={-20} /></ChartCard>
              </>
            )}
          </div>
        </>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-sky/70 bg-white" data-testid="sp-table">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky/60 p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {QUICK.map(([v, l]) => <button key={v} onClick={() => setQuick(v)} data-testid={`quick-${v || "all"}`} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${quick === v ? "bg-loop text-white" : "border border-sky text-ink/60 hover:bg-mist"}`}>{l}</button>)}
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sp-sort" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
            <option value="score_asc">Score ↑</option><option value="score_desc">Score ↓</option>
            <option value="nc_desc">Not-contacted % ↓</option><option value="overdue_desc">Overdue ↓</option>
            <option value="sla_desc">SLA breach % ↓</option><option value="assigned_desc">Assigned ↓</option>
          </select>
          <button onClick={() => setDynOn((v) => !v)} data-testid="sp-dyn-toggle" title={dynCfg ? `${dynCfg.name} v${dynCfg.version}` : "Use the Admin-configured dynamic scoring engine"}
            className={`h-9 rounded-lg px-3 text-xs font-semibold transition-colors ${dynOn ? "bg-loop text-white" : "border border-loop/40 text-loop hover:bg-loop/5"}`}>
            {dynOn ? `Dynamic engine: ON${dynCfg ? ` · v${dynCfg.version}` : ""}` : "Dynamic engine (beta)"}
          </button>
        </div>
        {!rows ? <div className="flex justify-center py-12"><Loader2 className="h-4 w-4 animate-spin text-ink/40" /></div>
          : !rows.length ? <EmptyState title="No report data found for the selected filters." />
          : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-mist/70 text-left text-ink/50"><tr>
                  {["Salesperson", "Manager", "Assigned", "Contacted %", "NC %", "Avg 1st Resp", "SLA Breach %", "Overdue", "FU Compl %", "Mtg Sched", "Mtg Done", "Moved Fwd", "Not Picked %", "Call Back Later %", "Yet To Qualify %", "Junk %", "Lost %", "Score", ...(dynOn ? ["Dynamic", "Diagnosis"] : [])].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-sky/40">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-sky/10" data-testid={`sp-row-${r.id}`}>
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-navy">{r.name}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-ink/60">{r.manager}</td>
                      <td className="px-3 py-2 text-ink/70">{r.assigned}</td>
                      <td className="px-3 py-2 text-emerald-600">{pct(r.contacted_pct)}</td>
                      <td className={`px-3 py-2 ${r.nc_pct >= 40 ? "font-semibold text-red-500" : "text-ink/70"}`}>{pct(r.nc_pct)}</td>
                      <td className="px-3 py-2 text-ink/60">{fmtMins(r.avg_first_response_min)}</td>
                      <td className={`px-3 py-2 ${r.sla_breach_pct >= 30 ? "font-semibold text-orange-500" : "text-ink/70"}`}>{pct(r.sla_breach_pct)}</td>
                      <td className={`px-3 py-2 ${r.overdue >= 5 ? "font-semibold text-amber-600" : "text-ink/70"}`}>{r.overdue}</td>
                      <td className="px-3 py-2 text-ink/70">{pct(r.fu_completion_pct)}</td>
                      <td className="px-3 py-2 text-ink/70">{r.mtg_sched}</td>
                      <td className="px-3 py-2 text-ink/70">{r.mtg_done}</td>
                      <td className="px-3 py-2 text-ink/70">{r.moved_forward}</td>
                      <td className={`px-3 py-2 ${r.not_picked_pct >= 40 ? "font-semibold text-red-500" : "text-ink/70"}`} data-testid={`sp-notpicked-${r.id}`}>{pct(r.not_picked_pct)}</td>
                      <td className="px-3 py-2 text-amber-600" data-testid={`sp-callback-${r.id}`}>{pct(r.call_back_later_pct)}</td>
                      <td className="px-3 py-2 text-ink/70" data-testid={`sp-yettoqualify-${r.id}`}>{pct(r.yet_to_qualify_pct)}</td>
                      <td className="px-3 py-2 text-ink/60">{pct(r.junk_pct)}</td>
                      <td className="px-3 py-2 text-ink/60">{pct(r.lost_pct)}</td>
                      <td className={`px-3 py-2 font-bold ${scoreColor(r.score)}`} title={r.score == null ? "Fewer than 5 assigned leads" : breakdownTip(r.score_breakdown)} data-testid={`sp-score-${r.id}`}>
                        <span className="cursor-help">{r.score == null ? "—" : r.score}</span>
                        <span className={`block text-[10px] font-medium ${labelTone(r.score_label)}`}>{r.score_label}</span>
                      </td>
                      {dynOn && (() => { const d = dynMap[r.id]; return (
                        <>
                          <td className={`px-3 py-2 font-bold ${scoreColor(d?.score)}`} title={d ? dynTip(d.breakdown) : "No dynamic score"} data-testid={`sp-dyn-${r.id}`}>
                            <span className="cursor-help">{d?.insufficient || d?.score == null ? "—" : d.score}</span>
                            <span className={`block text-[10px] font-medium ${labelTone(d?.label)}`}>{d?.label || "—"}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {(d?.flags || []).slice(0, 3).map((f) => (
                                <span key={f.key} title={`${f.diagnosis || ""}${f.recommended_action ? "\n→ " + f.recommended_action : ""}`}
                                  className={`cursor-help rounded px-1.5 py-0.5 text-[10px] font-semibold ${f.severity === "critical" ? "bg-red-50 text-red-600" : f.severity === "warning" ? "bg-amber-50 text-amber-600" : f.severity === "positive" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{f.label}</span>
                              ))}
                              {!(d?.flags || []).length && <span className="text-[10px] text-ink/30">—</span>}
                            </div>
                          </td>
                        </>
                      ); })()}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        {rows?.length > 0 && (
          <div className="flex items-center justify-between border-t border-sky/60 p-3 text-xs text-ink/60">
            <span>{tmeta.total} salespersons · page {tmeta.page} of {tmeta.pages}</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev" className="rounded-lg border border-sky p-1.5 disabled:opacity-40 hover:bg-mist"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= tmeta.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next" className="rounded-lg border border-sky p-1.5 disabled:opacity-40 hover:bg-mist"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
